import { Autocomplete } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { defaultInnerStyle } from "Common/Components/PageContainer/PageContainer";
import { MecordColors } from "Constants/Color";
import CustomStyle, { autocompleteDropdownStyles } from "Constants/Style";
import { ClipboardList, Clock, FileCheck } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getWarehouseByUser,
  fetchItemInwardAnalytics,
} from "slices/Masters/warehosue/thunk";
import WarehouseGRN from "./GRN/GRN";
import PendingPO from "./PendingPO/PendingPO";
import ReturnedAccessoriesTab from "./ReturnedAccessories/ReturnedAccessoriesTab";
import { clearAllTableStates } from "utils/clearTableStates";
import NoWarehousesMessage from "components/NoWarehouse/NoWarehouseMessage";

const WarehouseItemInward = () => {
  const dispatch = useDispatch<any>();
  const [warehouseId, setWarehouseId] = useState<any>(
    sessionStorage.getItem("itemInwardWarehouse") || null,
  );
  const [tab, setTab] = useState(
    Number(sessionStorage?.getItem("WarehouseItemInwardTab")) || 1,
  );

  const { warehouseByUserList, warehouseByUserLoading } = useSelector(
    (state: any) => state.warehouse,
  );

  useEffect(() => {
    if (!warehouseByUserList?.length) {
      setWarehouseId(null);
      sessionStorage.removeItem("itemInwardWarehouse");
      return;
    }

    if (warehouseId == null) {
      setWarehouseId(warehouseByUserList[0].warehouse_id);
    }
  }, [warehouseByUserList]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      const newTab = newValue + 1;
      setTab(newTab);
      clearAllTableStates();
      sessionStorage.setItem("WarehouseItemInwardTab", newTab.toString());
    },
    [],
  );

  const [analyticsCounts, setAnalyticsCounts] = useState<any>({
    total_po_count: 0,
    pendingInward: 0,
    inventory_total_count: 0,
  });

  const statuses = useMemo(
    () => [
      {
        label: "Total POs",
        icon: <ClipboardList size={16} />,
        bgColor: "bg-blue-50",
        textColor: "text-blue-600",
        count: analyticsCounts.total_po_count || 0,
      },
      {
        label: "Pending Inward",
        icon: <Clock size={16} />,
        bgColor: "bg-yellow-50",
        textColor: "text-yellow-600",
        count: analyticsCounts.pendingInward,
      },
      {
        label: "GRN Created",
        icon: <FileCheck size={16} />,
        bgColor: "bg-green-50",
        textColor: "text-green-600",
        count: analyticsCounts.inventory_total_count,
      },
    ],
    [analyticsCounts],
  );

  useEffect(() => {
    if (!warehouseId) {
      setAnalyticsCounts({
        total_po_count: 0,
        pendingInward: 0,
        inventory_total_count: 0,
      });
      return;
    }
    let mounted = true;
    const fetchAnalytics = async () => {
      try {
        const result: any = await dispatch(
          fetchItemInwardAnalytics(warehouseId),
        );
        const payload = result?.payload ?? result;
        const data = payload?.data ?? payload ?? {};

        if (!mounted) return;

        setAnalyticsCounts({
          total_po_count: Number(data.total_po_count ?? 0),
          pendingInward:
            Number(data.pending_po_count ?? 0) +
            Number(data.partially_inwarded_count ?? 0),
          inventory_total_count: Number(data.inventory_total_count ?? 0),
        });
      } catch (err) {
        // ignore failures for now
      }
    };

    fetchAnalytics();

    return () => {
      mounted = false;
    };
  }, [warehouseId, dispatch]);

  useEffect(() => {
    dispatch(getWarehouseByUser());
  }, [dispatch]);

  return (
    <div
      className="page-content px-3 mecord-kpi-list-page"
      style={{ "--mecord-table-offset": "400px" } as React.CSSProperties}
    >
      <div className="flex-shrink-0">
        <AnalyticsCards statuses={statuses} />
      </div>

      <div
        className="mecord-list-panel"
        style={{
          ...defaultInnerStyle,
          flex: "1 1 0",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="flex-shrink-0">
          <div className="mecord-toolbar-title">
            <h6>Item Inward</h6>
            <Autocomplete
              options={warehouseByUserList ?? []}
              loading={warehouseByUserLoading}
              getOptionLabel={(option: any) => option?.warehouse_name || ""}
              value={
                warehouseByUserList.find(
                  (opt: any) => opt.warehouse_id === warehouseId,
                ) ?? null
              }
              isOptionEqualToValue={(option: any, value: any) =>
                option.warehouse_id === value.warehouse_id
              }
              onChange={(_, selected) => {
                clearAllTableStates();
                setWarehouseId(selected?.warehouse_id ?? null);
                sessionStorage.setItem(
                  "itemInwardWarehouse",
                  selected?.warehouse_id,
                );
              }}
              disableClearable
              sx={{ ...CustomStyle?.singleselect, width: 280 }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option) =>
                autocompleteDropdownStyles.renderOption(
                  props,
                  option,
                  (opt) =>
                    typeof opt === "string"
                      ? opt
                      : String(opt.warehouse_name),
                )
              }
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  name="warehouse"
                  placeholder="Select Warehouse"
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      "&:hover fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      height: 34,
                    },
                  }}
                />
              )}
            />
          </div>

          <div className="mecord-tabs-wrap">
            <Box sx={{ width: "100%" }}>
              <Tabs
                value={tab - 1}
                onChange={handleTabChange}
                sx={{
                  minHeight: 30,
                  "& .MuiTab-root": {
                    fontFamily: "'Poppins', sans-serif",
                    textTransform: "none",
                    color: "#555555",
                    minHeight: 30,
                    paddingTop: "2px",
                    paddingBottom: "2px",
                    transition: "color 0.2s ease",
                  },
                  "& .MuiTab-root:hover": {
                    color: "#d2363a",
                  },
                  "& .Mui-selected": {
                    color: "#d2363a !important",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#d2363a",
                    height: "3px",
                    transition: "all 0.3s ease",
                  },
                }}
              >
                <Tab label="Pending PO" />
                <Tab label="Pending RA" />
                <Tab label="GRN" />
              </Tabs>
              <Divider sx={{ mt: 0, borderColor: "#c35f61" }} />
            </Box>
          </div>
        </div>

        <div
          className="d-flex flex-column"
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            maxHeight: "100%",
            overflow: "hidden",
          }}
        >
          {warehouseByUserList.length === 0 ? (
            <NoWarehousesMessage />
          ) : tab === 1 ? (
            <PendingPO
              key={`pendingPO-${warehouseId}`}
              warehouseId={warehouseId}
            />
          ) : tab === 2 ? (
            <ReturnedAccessoriesTab
              key={`ret-acc-${warehouseId}`}
              warehouseId={warehouseId}
            />
          ) : tab === 3 ? (
            <WarehouseGRN
              key={`grn-${warehouseId}`}
              warehouseId={warehouseId}
            />
          )  : null}
        </div>
      </div>
    </div>
  );
};

export default WarehouseItemInward;
